import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../services/api';

const STORAGE_KEY = 'leasertech_site_settings';

const defaultSettings = {
  site: {
    name: 'LeaserTech',
    description: 'Elektronik ürünler için güvenilir alışveriş',
    currency: 'TRY',
    language: 'tr'
  },
  shipping: {
    freeShippingThreshold: 300,
    defaultShippingCost: 50,
    expressShippingCost: 249,
    estimatedDeliveryDays: 3
  },
  notifications: {
    newOrderEmail: true,
    lowStockAlert: true,
    userRegistrationEmail: true,
    orderStatusEmail: true
  },
  security: {
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireEmailVerification: true,
    twoFactorAuth: false
  }
};

const parseStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      settings: parsed.settings || parsed,
      meta: parsed.meta || { updatedAt: null }
    };
  } catch {
    return null;
  }
};

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [meta, setMeta] = useState({ updatedAt: null });
  const [loading, setLoading] = useState(true);

  const applySettings = useCallback((nextSettings, nextMeta) => {
    setSettings(nextSettings);
    setMeta(nextMeta || { updatedAt: null });
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ settings: nextSettings, meta: nextMeta || { updatedAt: null } })
    );
    localStorage.removeItem('siteSettings');
    localStorage.removeItem('adminSettings');
  }, []);

  const loadSettings = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      const response = await settingsAPI.getSettings();
      const payload = response.data;
      const nextSettings = payload.settings ?? payload;
      const nextMeta = payload.meta ?? { updatedAt: null };
      applySettings(nextSettings, nextMeta);
    } catch (error) {
      console.warn('Ayarlar API\'den yüklenemedi:', error);
      const cached = parseStored();
      if (cached?.settings) {
        setSettings(cached.settings);
        setMeta(cached.meta);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applySettings]);

  const updateSettings = useCallback(
    (nextSettings, nextMeta) => {
      applySettings(nextSettings, nextMeta);
    },
    [applySettings]
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const handleRefresh = () => loadSettings({ silent: true });
    window.addEventListener('leasertech-settings-updated', handleRefresh);
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) {
        loadSettings({ silent: true });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('leasertech-settings-updated', handleRefresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadSettings]);

  const value = {
    settings,
    meta,
    loading,
    loadSettings,
    updateSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
